import { useCallback } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { dismissNotification, markAllNotificationsAsRead } from "../api/notificationApi";
import { NOTIFICATIONS_QUERY_KEY } from "../constants";
import { markAllRead, removeNotification } from "../model/notificationCache";
import { useToast } from "../../shared/ToastProvider";

export function useNotificationActions() {
  const queryClient = useQueryClient();
  const { addToast } = useToast();
  const { t } = useTranslation();

  const applyOptimistic = useCallback(
    async (updater) => {
      await queryClient.cancelQueries({ queryKey: NOTIFICATIONS_QUERY_KEY });
      const previous = queryClient.getQueryData(NOTIFICATIONS_QUERY_KEY);
      queryClient.setQueryData(NOTIFICATIONS_QUERY_KEY, updater);
      return { previous };
    },
    [queryClient],
  );

  const rollback = useCallback(
    (context) => {
      if (context?.previous) {
        queryClient.setQueryData(NOTIFICATIONS_QUERY_KEY, context.previous);
      }
    },
    [queryClient],
  );

  const invalidate = useCallback(
    () => queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY }),
    [queryClient],
  );

  const dismissMutation = useMutation({
    mutationKey: ["notifications", "dismiss"],
    mutationFn: dismissNotification,
    onMutate: (notificationId) => applyOptimistic(removeNotification(notificationId)),
    onError: (_error, _notificationId, context) => {
      rollback(context);
      addToast(
        t("main.notifications.toasts.dismissFailed", {
          defaultValue: "Failed to view notification",
        }),
        "error",
      );
    },
    onSettled: invalidate,
  });

  const markAllMutation = useMutation({
    mutationKey: ["notifications", "markAllAsRead"],
    mutationFn: markAllNotificationsAsRead,
    onMutate: () => applyOptimistic(markAllRead()),
    onSuccess: () => {
      addToast(
        t("main.notifications.toasts.markAllSuccess", {
          defaultValue: "All notifications marked as read",
        }),
        "success",
      );
    },
    onError: (_error, _variables, context) => {
      rollback(context);
      addToast(
        t("main.notifications.toasts.markAllFailed", {
          defaultValue: "Failed to mark all as read",
        }),
        "error",
      );
    },
    onSettled: invalidate,
  });

  return {
    dismiss: dismissMutation.mutate,
    isDismissing: dismissMutation.isPending,
    markAllAsRead: markAllMutation.mutate,
    isMarkingAll: markAllMutation.isPending,
  };
}
